import { TemplateRef } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import {
  CellTemplateContext,
  DatasetTableConfig,
  SortChangeEvent,
  TableSortState,
} from '@ui';
import { ContactIdDbService } from '../../services/contact-id-db.service';
import { IncidentModels } from '../../models/incident.model';
import { OrgUser } from '../../store/state/org-users/org-users.models';
import { resolveOrgUserDisplayName } from '../../utils/org-user-display.util';
import { AccountMapEntry } from '../../store/state/accounts-map/accounts-map.models';

export enum SortColumn {
  Incident = 'incidentTitle',
  Date = 'startTime',
  Priority = 'priorityOrder',
  Status = 'statusOrder',
  Operator = 'operatorSort',
  Organization = 'organizationSort',
  Account = 'accountSort',
}

export interface IncidentRow {
  id: number;
  incidentTitle: string;
  incidentSubtitle: string;
  startTime: number;
  dateText: string;
  timeText: string;
  priority: IncidentModels.Priority | null;
  priorityOrder: number;
  status: IncidentModels.Status;
  statusOrder: number;
  operatorName: string | null;
  operatorSort: string;
  organizationName: string;
  organizationSort: string;
  accountName: string;
  accountSort: string;
}

const PRIORITY_ORDER: Record<IncidentModels.Priority, number> = {
  [IncidentModels.Priority.Critical]: 0,
  [IncidentModels.Priority.High]: 1,
  [IncidentModels.Priority.Medium]: 2,
  [IncidentModels.Priority.Low]: 3,
  [IncidentModels.Priority.Info]: 4,
  [IncidentModels.Priority.Administrative]: 5,
};

export const DEFAULT_REPORTS_SORT_STATE: TableSortState = {
  key: SortColumn.Date,
  direction: -1,
};

export interface ReportsTableTemplates {
  incidentCell: TemplateRef<CellTemplateContext<IncidentRow>>;
  dateCell: TemplateRef<CellTemplateContext<IncidentRow>>;
  priorityCell: TemplateRef<CellTemplateContext<IncidentRow>>;
  statusCell: TemplateRef<CellTemplateContext<IncidentRow>>;
  operatorCell: TemplateRef<CellTemplateContext<IncidentRow>>;
  organizationCell: TemplateRef<CellTemplateContext<IncidentRow>>;
  accountCell: TemplateRef<CellTemplateContext<IncidentRow>>;
  onRowClick?: (row: IncidentRow) => void;
}

export function createReportsTableConfig(
  templates: ReportsTableTemplates,
): DatasetTableConfig<IncidentRow> {
  return {
    columns: [
      {
        key: SortColumn.Incident,
        label: 'REPORTS.TABLE.INCIDENT',
        width: 'minmax(160px, 1.8fr)',
        sortable: true,
        cellTemplate: templates.incidentCell,
        cardIcon: 'ui-icon-file',
        mobileVisible: true,
      },
      {
        key: SortColumn.Date,
        label: 'REPORTS.TABLE.DATE',
        width: 'minmax(112px, 0.95fr)',
        sortable: true,
        cellTemplate: templates.dateCell,
        mobileVisible: true,
        cardIcon: 'ui-icon-calendar',
      },
      {
        key: SortColumn.Priority,
        label: 'REPORTS.TABLE.PRIORITY',
        width: 'minmax(82px, 0.72fr)',
        sortable: true,
        cellTemplate: templates.priorityCell,
      },
      {
        key: SortColumn.Status,
        label: 'REPORTS.TABLE.STATUS',
        width: 'minmax(88px, 0.78fr)',
        sortable: true,
        cellTemplate: templates.statusCell,
      },
      {
        key: SortColumn.Operator,
        label: 'REPORTS.TABLE.OPERATOR',
        width: 'minmax(120px, 1.05fr)',
        sortable: true,
        cellTemplate: templates.operatorCell,
      },
      {
        key: SortColumn.Organization,
        label: 'REPORTS.TABLE.ORGANIZATION',
        width: 'minmax(128px, 1.1fr)',
        sortable: true,
        cellTemplate: templates.organizationCell,
      },
      {
        key: SortColumn.Account,
        label: 'REPORTS.TABLE.ACCOUNT',
        width: 'minmax(128px, 1.1fr)',
        sortable: true,
        cellTemplate: templates.accountCell,
      },
    ],
    rowHeight: 72,
    trackByKey: 'id',
    onRowClick: templates.onRowClick,
  };
}

export function mapIncidentsToReportRows(
  incidents: IncidentModels.Incident[],
  orgUsers: Dictionary<OrgUser> | null | undefined,
  accounts: Dictionary<AccountMapEntry> | null | undefined,
  contactIdDbService: ContactIdDbService,
  unknownEventLabel: string,
  unknownUserLabel: string,
  dateFormatter: Intl.DateTimeFormat,
  timeFormatter: Intl.DateTimeFormat,
): IncidentRow[] {
  return incidents.map((incident) =>
    mapIncidentToRow(
      incident,
      orgUsers,
      accounts,
      contactIdDbService,
      unknownEventLabel,
      unknownUserLabel,
      dateFormatter,
      timeFormatter,
    ),
  );
}

export function sortReportRows(
  rows: IncidentRow[],
  sortState: TableSortState,
): IncidentRow[] {
  const data = [...rows];
  const { key, direction } = sortState;

  if (!key || direction === 0) {
    return data;
  }

  return data.sort((left, right) => {
    const leftValue = getComparableValue(left, key as SortColumn);
    const rightValue = getComparableValue(right, key as SortColumn);

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return direction * (leftValue - rightValue);
    }

    return (
      direction *
      String(leftValue).localeCompare(String(rightValue), undefined, {
        sensitivity: 'base',
      })
    );
  });
}

export function getNextSortState(
  current: TableSortState,
  event: SortChangeEvent,
): TableSortState {
  const nextKey = event.columnKey as SortColumn;

  if (current.key === nextKey) {
    const nextDirection =
      current.direction === 1 ? -1 : current.direction === -1 ? 0 : 1;
    return { key: nextKey, direction: nextDirection };
  }

  return { key: nextKey, direction: 1 };
}

export function getPriorityClassForRow(
  row: IncidentRow,
): string {
  if (!row.priority) {
    return 'priority-chip priority-chip-unknown';
  }
  return `priority-chip priority-chip-${row.priority.toLowerCase()}`;
}

export function getPriorityLabelKeyForRow(
  row: IncidentRow,
): string {
  if (!row.priority) {
    return 'REPORTS.TABLE.UNKNOWN';
  }
  return IncidentModels.getPriorityTranslationKey(row.priority);
}

export function getStatusClassForRow(row: IncidentRow): string {
  const statusClass = IncidentModels.getStatusClass(row.status);
  return `status-chip ${statusClass}`;
}

export function getStatusLabelKeyForRow(
  row: IncidentRow,
): string {
  return IncidentModels.getStatusTranslationKey(row.status);
}

function mapIncidentToRow(
  incident: IncidentModels.Incident,
  orgUsers: Dictionary<OrgUser> | null | undefined,
  accounts: Dictionary<AccountMapEntry> | null | undefined,
  contactIdDbService: ContactIdDbService,
  unknownEventLabel: string,
  unknownUserLabel: string,
  dateFormatter: Intl.DateTimeFormat,
  timeFormatter: Intl.DateTimeFormat,
): IncidentRow {
  const firstManagerId = incident.managedBy?.[0] ?? null;
  const primaryContactCode = getPrimaryContactCode(incident.policyCodes);
  const eventClass =
    primaryContactCode !== null
      ? contactIdDbService.getClassLabel(primaryContactCode)
      : '';
  const eventTitle =
    contactIdDbService.getHighestPriorityTitle(incident.policyCodes) ??
    unknownEventLabel;

  const account = accounts?.[incident.accountId];
  const accountName = account?.parentAccountName ?? account?.name ?? incident.accountId;
  const organizationName = account?.organizationName ?? '';
  const operatorName = incident.pickerDisplayName
    ?? resolveOperatorName(firstManagerId, orgUsers, unknownUserLabel);
  const date = new Date(incident.startTime);
  const priority = incident.priority ?? null;

  return {
    id: incident.id,
    incidentTitle: eventTitle,
    incidentSubtitle: `${IncidentModels.formatId(incident.id)}${
      eventClass ? ` ${eventClass}` : ''
    }`,
    startTime: incident.startTime,
    dateText: dateFormatter.format(date),
    timeText: timeFormatter.format(date),
    priority,
    priorityOrder: getPriorityOrder(priority),
    status: incident.status,
    statusOrder: incident.status,
    operatorName,
    operatorSort: (operatorName ?? '').toLowerCase(),
    organizationName,
    organizationSort: organizationName.toLowerCase(),
    accountName,
    accountSort: accountName.toLowerCase(),
  };
}

function getComparableValue(
  row: IncidentRow,
  column: SortColumn,
): string | number {
  switch (column) {
    case SortColumn.Date:
      return row.startTime;
    case SortColumn.Priority:
      return row.priorityOrder;
    case SortColumn.Status:
      return row.statusOrder;
    case SortColumn.Operator:
      return row.operatorSort;
    case SortColumn.Organization:
      return row.organizationSort;
    case SortColumn.Account:
      return row.accountSort;
    case SortColumn.Incident:
    default:
      return row.incidentTitle;
  }
}

function getPrimaryContactCode(contactIds?: number[]): number | null {
  if (!contactIds?.length) {
    return null;
  }
  return Math.min(...contactIds);
}

function resolveOperatorName(
  managerId: string | null,
  entities: Dictionary<OrgUser> | null | undefined,
  unknownUserLabel: string,
): string | null {
  if (!managerId) {
    return null;
  }

  return resolveOrgUserDisplayName(managerId, entities) || unknownUserLabel;
}

function getPriorityOrder(priority: IncidentModels.Priority | null): number {
  if (!priority) {
    return Number.MAX_SAFE_INTEGER;
  }
  return PRIORITY_ORDER[priority];
}
